import HttpFactory from '../factory'
import type { ObservationDefinition } from '../types/directus/observationDefinition'

interface ICmsInput {
  collection: string
  query: string
}

class CmsModule extends HttpFactory<any[]> {
  private RESOURCE = '/items'
  private RESOURCE_TRANSLATIONS = '/translations'
  private OBSERVATION_CATEGORY = '/fields/observations'
  private CMS_ENV: any = {
    production: ['production'],
    staging: ['production', 'staging'],
    develop: ['production', 'staging', 'develop'],
  }
  private SETTING_ENV: any = {
    production: 'main',
    staging: 'staging',
    develop: 'develop',
  }
  async getAll(cmsInput: ICmsInput): Promise<any> {
    return await this.call<any>('GET', `${this.RESOURCE}/${cmsInput.collection}?${cmsInput.query}`)
  }

  async get(collection: any, query: any) {
    return await this.call<any>('GET', `${this.RESOURCE}/${collection}?${query}`)
  }

  async getConfig() {
    let query = ''
    const { ENVIRONMENT } = useEnvironment()

    if (ENVIRONMENT !== 'production') {
      query = `?version=${this.SETTING_ENV[ENVIRONMENT]}`
    }
    return await this.call<any>('GET', `${this.RESOURCE}/settings${query}`)
  }

  async getUserRoles() {
    const cmsInput = {
      collection: 'users_roles',
      query: `fields[]=*&fields[]=translations.*`,
    }
    const result = await this.call<any>('GET', `${this.RESOURCE}/${cmsInput.collection}?${cmsInput.query}`)
    return result.data
  }

  async getTranslations() {
    return await this.call<any>('GET', `/${this.RESOURCE_TRANSLATIONS}`)
  }

  async getRelationship(): Promise<any> {
    const { ENVIRONMENT } = useEnvironment()
    const cmsInput = {
      collection: 'relationships',
      query: `fields[]=*&fields[]=translations.*&filter[status][_in]=${this.CMS_ENV[ENVIRONMENT]}`,
    }
    const result = await this.call<any>('GET', `${this.RESOURCE}/${cmsInput.collection}?${cmsInput.query}`)
    return result.data
  }

  async getPractitioners(): Promise<any> {
    const { ENVIRONMENT } = useEnvironment()
    const cmsInput = {
      collection: 'practitioners',
      query: `fields[]=*&limit=10000&fields[]=translations.*&fields[]=specialties.*&fields[]=specialties.systems.*&filter[status][_in]=${this.CMS_ENV[ENVIRONMENT]}`,
    }
    const result = await this.call<any>('GET', `${this.RESOURCE}/${cmsInput.collection}?${cmsInput.query}`)
    return result.data
  }

  async getPractitionersSpecialities(): Promise<any> {
    const { ENVIRONMENT } = useEnvironment()
    const cmsInput = {
      collection: 'practitioners_specialties',
      query: `fields[]=*&limit=10000&fields[]=translations.*&filter[status][_in]=${this.CMS_ENV[ENVIRONMENT]}`,
    }
    const result = await this.call<any>('GET', `${this.RESOURCE}/${cmsInput.collection}?${cmsInput.query}`)
    return result.data
  }

  async getOrganisation(): Promise<any> {
    const { ENVIRONMENT } = useEnvironment()
    const cmsInput = {
      collection: 'organizations',
      query: `fields[]=*&limit=10000&fields[]=translations.*&filter[status][_in]=${this.CMS_ENV[ENVIRONMENT]}`,
    }
    const result = await this.call<any>('GET', `${this.RESOURCE}/${cmsInput.collection}?${cmsInput.query}`)
    return result.data
  }

  async getObservationDefinitionsForChart() {
    const { ENVIRONMENT } = useEnvironment()

    const vitalsCategory: string[] = ['vital-signs', 'laboratory', 'survey']

    const vitalsSignsDefinitionData = await this.get(
      'observations',
      `fields[]=*&limit=10000&fields[]=translations.*&filter[status][_in]=${this.CMS_ENV[ENVIRONMENT]}&filter[category][_in]=${vitalsCategory}`,
    )
    return parseObservationDefinitionData(vitalsSignsDefinitionData.data)
  }

  async getObservationDefinitions(): Promise<ObservationDefinition[]> {
    const { ENVIRONMENT } = useEnvironment()

    const observationFilterEnv: any = {
      production: ['production', 'archived'],
      staging: ['production', 'staging', 'archived'],
      develop: ['production', 'staging', 'develop', 'archived'],
    }

    const definitionData = await this.get(
      'observations',
      `fields[]=*&limit=10000&fields[]=translations.*&filter[status][_in]=${observationFilterEnv[ENVIRONMENT]}`,
    )
    return definitionData.data
  }

  async getDefinitionsByCodes(codes: string[]) {
    const { ENVIRONMENT } = useEnvironment()

    return await this.get(
      'observations',
      `fields[]=*&limit=10000&fields[]=translations.*&filter[status][_in]=${this.CMS_ENV[ENVIRONMENT]}&filter[code][_in]=${codes}`,
    )
  }

  async getObservationCategory() {
    return await this.call<any>('GET', `${this.OBSERVATION_CATEGORY}/category`)
  }

  async getPatientFollowUpTask() {
    return await this.call<any>(
      'GET',
      `${this.RESOURCE}/patients_followup_tasks_add?fields[]=items,items.translations.title,items.category,items.subitems.translations.title`,
    )
  }

  async getPatientFollowUpIncidentRechecks() {
    return await this.call<any>(
      'GET',
      `${this.RESOURCE}/patients_followup_incident_rechecks?fields[]=translations.name&fields[]=translations.id&fields[]=value&fields[]=id&fields[]=sort&filter[_and][0][parent]=patients_followup_incident`,
    )
  }
}

export default CmsModule
